/**
 * Custom hook for determining week start preference and work week days
 *
 * Priority order for week start:
 * 1. API response (most accurate)
 * 2. localStorage (user preference from calendar page)
 * 3. User location setting
 * 4. Default to Monday (1)
 */

import { useMemo } from 'react'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAuthUserSlice } from '../../../../../store/auth-user-slice'
import { PTOSuggestionsResponse } from '../../../types'

interface UseWeekPreferencesParams {
  ptoSuggestions: PTOSuggestionsResponse
}

interface UseWeekPreferencesReturn {
  weekStartsOn: 0 | 1
  workWeekDays?: number[]
}

/**
 * Hook that determines week start preference and work week days from various sources
 *
 * @param ptoSuggestions - The PTO suggestions response containing context info
 * @returns Object with weekStartsOn (0 or 1) and optional workWeekDays array
 *
 * @example
 * const { weekStartsOn, workWeekDays } = useWeekPreferences({ ptoSuggestions })
 * // weekStartsOn: 1 (Monday)
 * // workWeekDays: [1, 2, 3, 4, 5] (Monday-Friday)
 */
export function useWeekPreferences({
  ptoSuggestions,
}: UseWeekPreferencesParams): UseWeekPreferencesReturn {
  const { authUser } = useAppSelector(selectAuthUserSlice)

  const weekStartsOn = useMemo(() => {
    // Check API response first (most accurate)
    const apiFirstDayOfWeek = ptoSuggestions.contextInfo?.meta?.firstDayOfWeek
    if (apiFirstDayOfWeek !== undefined) {
      return apiFirstDayOfWeek as 0 | 1
    }

    // Check localStorage (same as calendar page)
    const calendarStartOfWeek = localStorage.getItem('calendarStartOfWeek')
    if (calendarStartOfWeek === 'sun-thu') {
      return 0 // Sunday
    }
    if (calendarStartOfWeek === 'mon-fri') {
      return 1 // Monday
    }

    // Check location firstDayOfWeek (0 = Sunday, 1 = Monday)
    if (authUser?.location?.firstDayOfWeek !== undefined) {
      return authUser.location.firstDayOfWeek as 0 | 1
    }

    // Default to Monday
    return 1
  }, [authUser, ptoSuggestions.contextInfo?.meta?.firstDayOfWeek])

  const workWeekDays = useMemo(() => {
    return ptoSuggestions.contextInfo?.meta?.workWeekDays
  }, [ptoSuggestions.contextInfo?.meta?.workWeekDays])

  return {
    weekStartsOn,
    workWeekDays,
  }
}
